import Button from "@/common/components/Button";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { SearchIcon } from "lucide-react";
import { objectivePartsTableColumns } from "../../constants/objectivePartsTableColumns.constant";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { useNavigate } from "react-router-dom";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { MaintenanceObjectivePartsTransformer } from "../../services/maintenanceObjectivePartsTransformer";
import { useMaintenanceObjectiveParts } from "../../hooks/useMaintenanceObjectiveParts";
import ListFilters from "@/common/components/ListFilters";
import { useMaintenanceObjectiveSystems } from "../../../objectiveSystems/hooks/useMaintenanceObjectiveSystems";
import { MaintenanceObjectiveSystemData } from "../../../objectiveSystems/types/MaintenanceObjectiveSystem.type";

const MaintenanceObjectivePartsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const navigate = useNavigate();

  const [search, setSearch] = useDebounce(filterParams.search);
  const [searchSystem, setSearchSystem] = useDebounce('');

  const {
    data: objectiveSystemsOptions,
  } = useMaintenanceObjectiveSystems({
    filters: {
      search: searchSystem,
    },
    mapper: Mapper.mapToOptions<MaintenanceObjectiveSystemData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const {
    data: maintenanceObjectiveParts,
    isPending: isMaintenanceObjectivePartsPending,
    meta: maintenanceObjectivePartsMeta,
    refetch: refetchMaintenanceObjectiveParts,
    error: maintenanceObjectivePartsError = {} as any,
  } = useMaintenanceObjectiveParts({
    filters: {
      search,
      page: filterParams.page || 1,
      objectiveSystem: filterParams.objective_system?.value,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceObjectivePartsTransformer.tableTransformer(),
    }),
  });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-156px)]"
      refetchFunction={refetchMaintenanceObjectiveParts}
      statusCode={maintenanceObjectivePartsError?.status}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <TextField
            name="search"
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-xl"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
            }}
            value={filterParams.search}
          />

          <div className="flex items-center justify-between">
            <ListFilters
              filters={[
                {
                  placeholder: 'Sistema',
                  options: [{ label: 'Todos', value: '' } , ...objectiveSystemsOptions],
                  onChange: (value) => setFilterParams((prev) => ({ ...prev, objective_system: value })),
                  onInputChange: setSearchSystem,
                  value: filterParams.objective_system,
                },
              ]}
            />

            <Button
              name="create"
              operationKlassToCheckPermission="Admin::Maintenance::ObjectiveParts::Create"
              onClick={() => navigate('create')}
              className="ml-auto"
            >
              Nova Parte
            </Button>
          </div>
        </div>

        <Table
          className="h-[calc(100vh-320px)]"
          columns={objectivePartsTableColumns}
          rows={maintenanceObjectiveParts}
          page={Number(filterParams.page) || 1}
          totalPages={maintenanceObjectivePartsMeta.total_pages}
          totalCount={maintenanceObjectivePartsMeta.total}
          hasPagination
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          rowEmptyValue="--"
          isLoadingData={isMaintenanceObjectivePartsPending}
        />
      </div>
    </ErrorMessageWrapper>
  );
}

export default MaintenanceObjectivePartsList;
